













































































































































import { computed, defineComponent, onMounted, reactive, ref, watch } from '@vue/composition-api';
import OlbRangeSlider from '../form/elements/input/RangeSlider.vue';
import OlbInput from '../form/elements/input/Input.vue';
import OlbCurrencyInput from '../form/elements/input/CurrencyInput.vue';
import { OlbTabs, OlbTab } from '../tabs';
import OlbButton from '../button';
import OlbIcon from '../icon';
import OlbDialog from '../dialog';
import OlbLoader from '../loader';
import { populateVars, currencyWrap } from '../../utils/LegacyUtils';
import OlbTooltip from '../tooltip';
import OlbBaufiChart from './baufiChart.vue';
import OlbBaufiTable from './baufiTable.vue';

import { chunk, clamp, debounce } from 'lodash';
import axios from 'axios';

interface BaufiParameter {
	label: string;
	info: string;
	error: string;
	min: string;
	max: string;
	step: string;
	value: string;
	type: string;
}

type BaufiKey = 'objectValue' | 'equityValue' | 'interestDuration' | 'runtime';

export default defineComponent({
	components: {
		OlbBaufiChart,
		OlbBaufiTable,
		OlbTooltip,
		OlbRangeSlider,
		OlbInput,
		OlbCurrencyInput,
		OlbTabs,
		OlbTab,
		OlbButton,
		OlbIcon,
		OlbDialog,
		OlbLoader,
	},

	props: {
		data: {
			type: Object,
			required: true,
		},
	},

	setup(props) {
		let requestTimeout;

		const parameters = reactive<Record<BaufiKey, BaufiParameter>>({
			objectValue: {
				label: props.data.project.objectvalue.label,
				info: props.data.project.objectvalue.info,
				error: props.data.project.objectvalue.error,
				min: props.data.project.objectvalue.min,
				max: props.data.project.objectvalue.max,
				step: props.data.project.objectvalue.step,
				value: props.data.project.objectvalue.value,
				type: props.data.project.objectvalue.typ,
			},
			interestDuration: {
				label: props.data.project.fixedinterestrateduration.label,
				info: props.data.project.fixedinterestrateduration.info,
				error: props.data.project.fixedinterestrateduration.error,
				min: props.data.project.fixedinterestrateduration.min,
				max: props.data.project.fixedinterestrateduration.max,
				step: props.data.project.fixedinterestrateduration.step,
				value: props.data.project.fixedinterestrateduration.value,
				type: props.data.project.fixedinterestrateduration.typ,
			},
			equityValue: {
				label: props.data.project.equityvalue.label,
				info: props.data.project.equityvalue.info,
				error: props.data.project.equityvalue.error,
				min: props.data.project.equityvalue.min,
				max: props.data.project.equityvalue.max,
				step: props.data.project.equityvalue.step,
				value: props.data.project.equityvalue.value,
				type: props.data.project.equityvalue.typ,
			},
			runtime: {
				label: props.data.project.duration.label,
				info: props.data.project.duration.info,
				error: props.data.project.duration.error,
				min: props.data.project.duration.min,
				max: props.data.project.duration.max,
				step: props.data.project.duration.step,
				value: props.data.project.duration.value,
				type: props.data.project.duration.typ,
			},
		});

		const loading = ref(true);
		const interestDurationLimit = ref(false);
		const equityValueLimit = ref(false);
		const initiallyLoaded = ref(false)
		const responseData = ref<any>({});
		const series = ref<any[]>([]);

		function roundIncrement(number: number, increment: number) {
			return Math.ceil(number / increment) * increment;
		}

		const clampValue = (passedValue: string, key: BaufiKey) => {
			const value = parseInt(passedValue.replace('.', '').replace(',', '.'));
			const min = parseInt(parameters[key].min);
			const max = parseInt(parameters[key].max);

			if (isNaN(value)) {
				parameters[key].value = min.toString();
				return;
			}

			if (value < min || value > max) {
				parameters[key].value = clamp(value, min, max).toString();
				return;
			}

			parameters[key].value = roundIncrement(value, parseInt(parameters[key].step)).toString();
		};

		const isCurrency = (param: BaufiParameter) => param.type === 'EUR';

		const getRawValue = (value: string) => {
			return value.replaceAll('.', '').replace(',', '.');
		};

		const fetchData = async () => {
			const hasEmptyValue =
				[
					parameters.objectValue.value,
					parameters.equityValue.value,
					parameters.interestDuration.value,
					parameters.runtime.value,
				].filter((v) => !v).length > 0;

			if (hasEmptyValue) return;

			requestTimeout = setTimeout(() => {
				loading.value = true;
			}, 60);

			const response = await axios({
				method: props.data.method,
				url: props.data.action,
				params: {
					objectvalue: getRawValue(parameters.objectValue.value),
					equityvalue: getRawValue(parameters.equityValue.value),
					fixedinterestrateduration: getRawValue(parameters.interestDuration.value),
					duration: getRawValue(parameters.runtime.value),
				},
			});

			clearTimeout(requestTimeout)

			responseData.value = response.data as any;
			const plan = responseData.value.best.redemptionPlan;
			const initialAmount = parseFloat(getRawValue(responseData.value.financingAmount));
			series.value = [
				{ x: 0, y: initialAmount },
				...plan.map((i: any, index: number) => ({
					x: index + 1,
					y: i.remainingDept.replaceAll('.', ''),
					text: i.text,
				})),
			];
			loading.value = false;
			initiallyLoaded.value = true;
		};

		watch(
			() => [
				parameters.objectValue.value,
				parameters.equityValue.value,
				parameters.interestDuration.value,
				parameters.runtime.value,
			],
			debounce(async () => {
				await fetchData();
				interestDurationLimit.value = Number(parameters.interestDuration.value) > Number(parameters.runtime.value);
				equityValueLimit.value = Number(parameters.equityValue.value) === Number(parameters.objectValue.value) || Number(parameters.equityValue.value) > Number(parameters.objectValue.value);
			}, 500),
			{ immediate: true },
		);

		const tables = ref([
			[
				{
					key: 'interestrate',
					replace: {},
				},
				{
					key: 'annuity',
					replace: {},
				},
				{
					key: 'annuitycount',
					replace: {},
				},
				{
					key: 'effectiverate',
					replace: {},
				},
			],
			[
				{
					key: 'financingamount',
					replace: {},
				},
				{
					key: 'financingamounttotal',
					replace: {},
				},
				{
					key: 'remainingdeptfixedinterestrateduration',
					replace: {},
				},
				{
					key: 'redemptionfixedinterestrateduration',
					replace: {},
				},
			],
		]);

		return {
			clampValue,
			isCurrency,
			populateVars,
			currencyWrap,
			parameters,
			loading,
			responseData,
			series,
			tables,
			initiallyLoaded,
			interestDurationLimit,
			equityValueLimit
		};
	},
});
